import { InputNumber, Radio, Select } from 'antd'
import {
  CLASSIFICATION_UI,
  ClassificationQuestionMeta,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsClassificationUi'
import { ClassificationFacts } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsComponentClassification'
import { CitedHelp } from 'features/libra/stepComponents/ffs/CitedHelp'
import {
  Label,
  Row,
  SELECT_STYLE,
} from 'features/libra/stepComponents/ffs/ltaFormLayout'
import { useGeckoLocale } from 'features/locale'
import { FC } from 'react'
import { GeckoMeasurement } from 'types'

export interface ClassificationFactInputProps {
  fact: keyof ClassificationFacts
  value: ClassificationFacts[keyof ClassificationFacts]
  readonly: boolean
  onChange: (fact: keyof ClassificationFacts, value: unknown) => void
}

/**
 * One question in the FFS pre-planning questionnaire, rendered entirely from
 * `CLASSIFICATION_UI` metadata.
 *
 * Because that metadata is keyed by the fact union and discriminated on `kind`,
 * this component needs no key-presence guard and no per-fact branching to find
 * option help — `meta` and `meta.help` are guaranteed to exist, and `optionHelp`
 * exists exactly when there are `options` to explain.
 */
export const ClassificationFactInput: FC<ClassificationFactInputProps> = ({
  fact,
  value,
  readonly,
  onChange,
}) => {
  // `fact` is a variable here, so the mapped type widens to the union of arms —
  // hence the local alias rather than per-fact narrowing.
  const meta: ClassificationQuestionMeta = CLASSIFICATION_UI[fact]
  const { getPreferredMeasurementUnit } = useGeckoLocale()

  let input: React.ReactNode
  switch (meta.kind) {
    case 'enum': {
      // A fact's OFFERED options can be narrower than its value union — `region`
      // presents only shell body. antd renders a value that is absent from
      // `options` as the raw slug ("nozzle" rather than "Nozzle"), so a saved
      // record using a no-longer-offered value would look broken. Re-add it,
      // labelled, so it displays properly and stays selectable until changed.
      const selected = typeof value === 'string' ? value : undefined
      const options =
        selected && !meta.options.some((o) => o.value === selected)
          ? [
              ...meta.options,
              {
                value: selected,
                label: meta.optionHelp[selected]?.label ?? selected,
              },
            ]
          : meta.options
      input = (
        <Select
          style={SELECT_STYLE}
          placeholder='Select'
          value={selected}
          onChange={(v) => onChange(fact, v)}
          options={options}
          disabled={readonly}
        />
      )
      break
    }
    case 'boolean':
      input = (
        <Radio.Group
          value={value as boolean}
          onChange={(e) => onChange(fact, e.target.value)}
          disabled={readonly}>
          <Radio value={true}>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
      )
      break
    case 'number': {
      const canonicalUnit = meta.canonicalUnit
      if (canonicalUnit) {
        // Stored/compared in the canonical unit; rendered and edited in the
        // preferred unit. Convert display -> canonical WITHOUT rounding first, so
        // repeated edits don't drift the stored value.
        const displayUnit = getPreferredMeasurementUnit(canonicalUnit)
        const displayValue =
          typeof value === 'number'
            ? GeckoMeasurement.CreateFromValue(value, canonicalUnit).toNumber(
                displayUnit
              )
            : null
        input = (
          <InputNumber
            value={displayValue}
            onChange={(v) =>
              onChange(
                fact,
                typeof v === 'number'
                  ? GeckoMeasurement.CreateFromValue(v, displayUnit).toNumber(
                      canonicalUnit
                    )
                  : undefined
              )
            }
            addonAfter={displayUnit.name}
            disabled={readonly}
          />
        )
      } else {
        input = (
          <InputNumber
            value={value as number}
            onChange={(v) => onChange(fact, v ?? undefined)}
            disabled={readonly}
          />
        )
      }
      break
    }
  }

  // Help for the SELECTED option. `optionHelp` is total over the fact's own value
  // union, so the only miss possible is a value not yet chosen.
  const optionNote =
    meta.kind === 'enum' && typeof value === 'string'
      ? meta.optionHelp[value]
      : undefined

  return (
    <Row>
      <Label>
        {meta.prompt} <CitedHelp note={meta.help} />
      </Label>
      {input}
      {optionNote ? <CitedHelp note={optionNote} /> : null}
    </Row>
  )
}
